import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, output, untracked } from "@angular/core";
import { Router } from "@angular/router";
import { MealKitIconButtonComponent } from "@yoni/mealcomponents-lib";
import { FullAdmissionsData } from "../../Services/gestion.service";
import { QdmService, ServiceItem } from "../../Services/qdm.service";
import { PipeNumberFormat } from "../Pipes/PipeNumberFormat";

export interface AdmissionMealMomentTab {
  /** mealMoment.qdm_id (correspond à ServiceItem.mealtime_id) */
  id: number;
  realId: number | null;
  name: string;
  admissions: FullAdmissionsData[];
}

/**
 * Regroupe les admissions par moment repas (qdm_id), dans l'ordre d'apparition.
 * Partagé avec la page principale pour la règle d'auto-sélection.
 */
export function groupAdmissionsByMealMoment(admissions: FullAdmissionsData[]): AdmissionMealMomentTab[] {
  const tabs = new Map<number, AdmissionMealMomentTab>();
  for (const admission of admissions) {
    if (!admission.mealMoment) continue;
    const id = admission.mealMoment.qdm_id;
    if (!tabs.has(id)) {
      tabs.set(id, { id, realId: admission.mealMoment.id, name: admission.mealMoment.name, admissions: [] });
    }
    tabs.get(id)!.admissions.push(admission);
  }
  return Array.from(tabs.values());
}

@Component({
    selector: "admission-selector",
    imports: [MealKitIconButtonComponent, PipeNumberFormat],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
    <div
      class="w-full h-full overflow-none pb-32 md:pb-2 p-2 flex justify-center items-center bg-gris-4 z-20">
      <div
        class="bg-white w-full h-fit my-auto max-h-full max-w-[800px] rounded overflow-y-none flex flex-col border border-gris-3">
        <div class="w-full p-6 h-24 relative flex items-start flex-col"
          style="background-image: url('/assets/images/modules/legumes_plate.webp'); background-size: cover; background-position: center;">
          <mk-icon-button (click)="this.redirectToMain()" class="ml-auto"
            direction="rtl"
            icon="cancel" label="Retour au choix des restaurants" useSvg="false"/>
          </div>
          <!-- Système d'onglets pour les meal moments -->
          <div class="w-full flex border-b border-gris-3 p-2 gap-1 flex-wrap">
            @for (mealMoment of mealMoments(); track mealMoment.id; let i = $index) {
              <div
                class="py-2 px-4 cursor-pointer text-center font-semibold rounded border-b-4 bg-gris-4 transition"
                [class.border-orange-1]="selectedTabIndex() === i"
                [class.hover:border-orange-3]="selectedTabIndex() !== i"
                (click)="selectTab(i)">
                {{ mealMoment.name + ' (' + mealMoment.admissions.length + ')' }}
              </div>
            }
          </div>
    
          <!-- Contenu de l'onglet sélectionné -->
          @for (mealMoment of mealMoments(); track mealMoment.id; let i = $index) {
            <div
              [class.hidden]="selectedTabIndex() !== i"
              class="mb-6 p-2 overflow-y-auto">
              <div [class.md:grid-cols-3]="mealMoment.admissions.length > 1"
                class="grid grid-cols-1 gap-4 justify-center place-items-center flex-1 min-h-96 max-h-96 h-96">
                @for (admission of mealMoment.admissions; track admission.admissionId) {
                  <div
                    class="rounded border border-gris-3 h-full flex flex-col w-full">
                    <div class="w-full bg-vert-3 fg-vert-1 py-3 px-2 flex items-center gap-2">
                      <img alt="Sac de fruits" class="h-8" src="/assets/images/plats/fruits_bag.webp"/>
                      <span class="font-semibold">{{ admission.admissionName }}</span>
                    </div>
                    <div class="flex-1 p-2 px-3 text-sm fg-noir-2">
                      <p class="font-semibold">{{ admission.admissionMax + ' composantes maximum' }}</p>
                      <ul class="list-disc list-inside">
                        @for (part of admission.admissionParts; track part) {
                          @if (!part.serviceIsNotInAdmission) {
                            <li [class.font-semibold]="part.isMandatory">
                              {{ part.quantity + ' ' + part.serviceName + ' ' + (part.isMandatory ? '(obligatoire)' : '') }}
                            </li>
                          }
                        }
                      </ul>
                    </div>
                    <div class="h-8 w-full p-2 py-6 flex items-center">
                      <span class="fg-gris-1 font-semibold">{{ (admission.finalPrice|formatNumber) + "€" }}</span>
                      <mk-icon-button (click)="this.selectAdmission(admission)" [height]="30" class="ml-auto text-sm"
                        direction="rtl" icon="keyboard_arrow_right"
                        label="Choisir" type="primary" useSvg="false"/>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>
        </div>
    `
})

export class AdmissionSelectorComponent {
  private readonly qdm = inject(QdmService);
  protected readonly router = inject(Router);

  readonly admissions = input.required<FullAdmissionsData[]>();
  readonly currentService = input<ServiceItem | undefined>(undefined);

  readonly admissionSelected = output<FullAdmissionsData>();
  readonly groupOrderQtyChanged = output<number>();

  protected readonly mealMoments = computed(() => groupAdmissionsByMealMoment(this.admissions()));

  /** Onglet du moment repas du service courant, sinon le premier. Modifiable au clic. */
  protected readonly selectedTabIndex = linkedSignal(() => {
    const mealtimeId = this.currentService()?.mealtime_id;
    const index = this.mealMoments().findIndex((m) => String(m.id) === String(mealtimeId));
    return index !== -1 ? index : 0;
  });

  // L'auto-sélection (une seule admission dans l'onglet) est gérée par la page parente
  // avant l'affichage de ce composant, pour éviter un affichage/masquage visible.

  constructor() {
    effect(() => {
      const mealMoments = this.mealMoments();
      if (mealMoments.length === 1 && mealMoments[0].realId == 0 && this.qdm.selectedRestaurant()) {
        untracked(() => this.router.navigate(["/error/no-mealmoments"]));
      }
    });
  }

  protected selectTab(index: number): void {
    this.selectedTabIndex.set(index);
  }

  selectAdmission(admission: FullAdmissionsData) {
    this.admissionSelected.emit(admission);
  }

  groupOrderQuantityChanged(groupOrderQty: number) {
    this.groupOrderQtyChanged.emit(groupOrderQty);
  }

  redirectToMain() {
    this.router.navigate(['/'])
  }
}
